import { Text } from '@mantine/core';
import type { CSSProperties, ReactNode } from 'react';
import { isChromelessWidgetType, Widget } from '../Widgets/types';
import {
  resolveAlertSeverityTint,
  resolveCustomWidgetTint,
  resolveWidgetTextColor,
  resolveWidgetTint,
  WIDGET_TINT_CLASS
} from './tints';
import { componentsTestIds } from '@components/testids';
import { subjectIcon } from '../Widgets/WebWidget/collectionIcon';
import type { TintSurfaces } from './tintColor';
import classes from './WidgetCard.module.css';

/**
 * WidgetCard — the per-card surface, SHARED by the editor and the viewer
 * (TV1-2291 · SDD §4, ticket 11).
 *
 * ⚠️ This is not tidying. A card TINT (ticket 13) has to render for READERS too,
 * so the surface cannot stay inline in `WidgetLayoutEditor`'s JSX as it did — a
 * reader would never get it. Ticket 12 rebuilds the chrome on design-system
 * tokens and ticket 13 adds the tints; both build on this one file rather than
 * on two copies that have to be kept in step by hand.
 *
 * WHAT IT OWNS: title, body slot, description — nothing else. It is handed the
 * widget it must draw and never reaches back into layout state; in particular it
 * knows nothing about the editor's two resolutions (SDD §1 / ticket 6 — the DOM
 * renders `displayWidgets`, mutations write `authoredWidgets`), so it cannot
 * confuse them.
 *
 * ⚠️ HOW A READER IS KEPT READ-ONLY. The interactive chrome arrives as an
 * OPTIONAL `chrome` SLOT, not as a `readOnly` / `editable` boolean.
 *
 *   - a slot the viewer never fills renders nothing, because there is nothing to
 *     render — the edit affordances do not exist in this module at all;
 *   - a boolean would put the affordances HERE and make "reader" the result of
 *     remembering to pass the flag, so the failure mode of forgetting is an
 *     edit button on a published report.
 *
 * The viewer therefore cannot acquire an edit affordance by omission, only by
 * someone deliberately writing one and passing it in.
 *
 * POSITIONING IS THE CALLER'S. The two renderers place a card differently — the
 * viewer's card IS the absolutely-positioned element (`widgetRectPx`), the
 * editor's fills a `<Rnd>` that owns the position — so placement stays in each
 * renderer's own module and arrives via `className` / `style`. What is shared is
 * the SURFACE (box model, padding, column flow), and that is what `.card` is.
 */
export type WidgetCardProps = {
  /**
   * The widget being drawn. Read for its title, description and type — and, on
   * the WRAPPER rather than the definition, its `tint` (t13, see `tints.ts`).
   */
  widget: Widget;
  /** The card body — the rendered widget component. */
  children: ReactNode;
  /**
   * Interactive editor chrome (today: the hover action bar). The VIEWER NEVER
   * PASSES THIS — see the read-only note above.
   */
  chrome?: ReactNode;
  /**
   * The card is the inspector's current selection (TV1-2291 · t12 · SDD §4).
   *
   * A pure VISUAL state — an inset accent ring, plus the action bar pinned open
   * so the controls do not vanish the moment the pointer leaves the card the
   * user is configuring. It is NOT an edit flag: passing `selected` grants no
   * affordance the `chrome` slot did not already carry, so the read-only
   * guarantee above is untouched (a viewer that passed `selected` would draw a
   * ring around a card with nothing in it).
   *
   * No selection SOURCE exists yet — ticket 17 wires the inspector to it. Until
   * then this is exercised only by the tests, deliberately: the styling and the
   * wiring are separate reviews.
   */
  selected?: boolean;
  /**
   * A colour-STEP surface, already derived (2026-08-19).
   *
   * ⚠️ Resolved by the RENDERER, not here, and that is not an oversight: a step is
   * matched against the tile's fetched VALUE, and the card is handed a widget
   * definition rather than a payload. The viewer and the editor are the only places
   * both are in scope, so they call `resolveWidgetStepTint( widget, data )` and pass
   * the result down.
   *
   * When present it replaces BOTH the alert severity surface and the author's custom
   * colour path — it lands in the same six inline custom properties, so the card
   * keeps one painting rule instead of growing a third.
   */
  stepTint?: TintSurfaces;
  /** Caller-owned placement class (the renderer's own `.widget`). */
  className?: string;
  /** Caller-owned placement style (the viewer's `widgetRectPx` rect). */
  style?: CSSProperties;
  /** From `componentsTestIds` — never a raw literal (`teselly/no-raw-testid`). */
  testId?: string;
};

export default function WidgetCard({
  widget,
  children,
  chrome,
  selected = false,
  stepTint,
  className,
  style,
  testId
}: WidgetCardProps ) {
  const definition = widget?.definition;

  // TV1-2291 (t21) — a CHROMELESS type draws its own tile and must not get this
  // header: `heading`'s title IS its content (it would print twice), and
  // `spacer` must draw nothing at all for a reader.
  //
  // Consulted HERE, once, rather than at each renderer: the editor and the
  // viewer used to carry the same condition, and the comment they both carried
  // said so — "kept in sync via `CHROMELESS_WIDGET_TYPES`, not by two copies of
  // the condition". Now that the header itself lives in one place, so does the
  // rule that suppresses it, and the two renderers cannot drift.
  const showTitle = Boolean( definition?.title ) && !isChromelessWidgetType( definition?.type );

  // TV1-2291 (t12) — `data-widget-card` is the HOOK THE CHROME HANGS OFF, and it
  // is an attribute rather than a class on purpose: CSS-module class names are
  // hashed per file, so `.card:hover .actions` cannot cross from this module into
  // `WidgetCardChrome.module.css`, where the action bar's own rules live. An
  // attribute selector is stable across modules, which is what lets the chrome
  // own BOTH its appearance and the condition it appears under, in one file.
  //
  // `data-selected` is on the same element for the same reason (the ring is
  // drawn here, the pinned-open action bar is drawn there) — and, being a
  // rendered attribute rather than a hashed class, it is also the honest thing
  // for a test to assert selection on.
  //
  // TV1-2291 (t13) — THE TINT. Read off the widget WRAPPER (`widget.tint`),
  // never off `widget.definition`: the definition is the LLM output gate and
  // `widgetGrammar.modelVariant()` inherits every top-level field it carries, so
  // a per-definition tint would hand a model a card-presentation key (see
  // `tints.ts`). `resolveWidgetTint` also drops it for a type that refuses one —
  // a chart CAN legitimately carry a persisted tint, because the refusal is a UI
  // rule and not a schema constraint, and the reader's path has no inspector in
  // it to explain that.
  const tint = resolveWidgetTint( widget );

  /*
   * The CUSTOM colour path (2026-08-17). Mutually exclusive with `tint` above —
   * `widget.tint` is either one of the seven names or a hex, never both.
   *
   * The six derived values go on as INLINE CUSTOM PROPERTIES rather than as a
   * computed background, so the stylesheet's `light-dark()` still does the theme
   * switch: a card must follow a theme change without re-rendering. That is also
   * why the pair is derived here and not in CSS — CSS cannot do the colour maths,
   * and JS cannot see the active theme without subscribing to it.
   */
  /*
   * A matching colour STEP outranks the author-picked colour path below only in the
   * sense that it FILLS it: both end up as the same six inline custom properties, so
   * the card has one painting rule rather than two. `stepTint` is resolved by the
   * renderer (it needs the fetched value, which the card does not have) and arrives
   * pre-derived — see `resolveWidgetStepTint`.
   */
  const custom = stepTint ?? resolveCustomWidgetTint( widget );

  /*
   * The author's own ink (2026-08-18). Independent of the tint above — a card can
   * have one, the other, both or neither — which is why it is read separately
   * rather than folded into the tint resolvers.
   */
  const textColor = resolveWidgetTextColor( widget );

  /*
   * The alert tile's severity surface (2026-08-18). Returns a class only for an
   * `alert` with NO author-set tint, so it never competes with `tint` / `custom`
   * above — see `resolveAlertSeverityTint`.
   */
  // A step that matched replaces the severity surface entirely: they answer the
  // same question (what colour is this tile) and the band is strictly more specific.
  const alertSeverityClass = stepTint ? undefined : resolveAlertSeverityTint( widget );

  /*
   * The subject icon that sits beside the title (2026-08-19).
   *
   * Only the two COUNT-SHAPED readmodel tiles get one — `alert` and `count` — and
   * they get different treatments for a reason that is not decoration: an alert's
   * mark carries SEVERITY and earns a tinted plate, while a count's says only "this
   * is what is being counted" and would be shouting if it did the same. `list` and
   * `progress` are excluded because neither is a single-number tile; an icon beside
   * their titles would be an unexplained ornament.
   *
   * Deliberately NOT driven by `collection` alone: a chart bound to a readmodel is
   * still a chart, and its title does not want a subject glyph.
   */
  const badgeType = definition?.type;
  const subjectBadge = ( badgeType === 'alert' || badgeType === 'count' )
    ? (
      <span
        className={`${ classes.cardBadge } ${ badgeType === 'alert' ? classes.cardBadgePlate : '' }`}
        aria-hidden="true"
        data-testid={badgeType === 'alert'
          ? componentsTestIds.alertWidgetBadge
          : componentsTestIds.countWidgetBadge}
      >
        {subjectIcon(
          ( definition as { collection?: string } | undefined )?.collection,
          badgeType === 'alert' ? 18 : 16
        )}
      </span>
    )
    : null;

  const cardClassName = [
    classes.card,
    selected ? classes.selected : null,
    tint ? WIDGET_TINT_CLASS[tint] : null,
    custom ? classes.tintCustom : null,
    alertSeverityClass ?? null,
    className ?? null
  ].filter( Boolean ).join( ' ' );

  const customTintStyle: CSSProperties | undefined = custom
    ? ({
      '--widget-tint-bg-light': custom.bgLight,
      '--widget-tint-bg-dark': custom.bgDark,
      '--widget-tint-border-light': custom.borderLight,
      '--widget-tint-border-dark': custom.borderDark,
      '--widget-tint-ink-light': custom.inkLight,
      '--widget-tint-ink-dark': custom.inkDark
    } as CSSProperties )
    : undefined;

  /*
   * Two properties, not one. `--widget-text-color` is the chosen ink;
   * `--widget-text-surface` is what the two SUBORDINATE weights fade toward, and
   * without it the `color-mix` in the stylesheet would have nothing to mix against
   * on an untinted card — the caption would come out identical to the value and the
   * hierarchy would flatten.
   *
   * The surface is the tint's own background when there is one, and the theme's
   * card surface otherwise. It is a `light-dark()` pair rather than a resolved
   * colour for the usual reason: the card must follow a theme change without
   * re-rendering.
   */
  const textColorStyle: CSSProperties | undefined = textColor
    ? ({
      '--widget-text-color': textColor,
      '--widget-text-surface': custom
        ? 'var(--widget-tint-bg)'
        : 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))'
    } as CSSProperties )
    : undefined;

  return (
    <div
      data-testid={testId}
      data-widget-card=""
      data-selected={selected ? 'true' : undefined}
      // Both the paint TRIGGER (`.card[data-widget-tint]` outranks any
      // single-class `.card` rule, so the tint cannot lose a source-order race
      // with ticket 12's surface) and the honest thing for a test to assert a
      // tint on — the class name it sits beside is hashed per build.
      // Present for a custom colour too — the attribute is the paint TRIGGER (it
      // outranks any single-class `.card` rule) and the honest thing for a test to
      // assert a tint on, and both of those are true whichever spelling was used.
      // The severity surface rides the same attribute, because the attribute is the
      // PAINT TRIGGER (`.card[data-widget-tint]`) and not a record of which field the
      // value came from. `'severity'` names the source so a test can tell the three
      // paths apart.
      data-widget-tint={tint ?? ( custom ? 'custom' : ( alertSeverityClass ? 'severity' : undefined ))}
      // The paint TRIGGER for the author's ink, same construction as the tint
      // attribute above: present only when a colour was actually chosen, so a card
      // without one matches nothing and keeps the theme-switching default.
      data-widget-text={textColor ? '' : undefined}
      className={cardClassName}
      style={{ ...style, ...customTintStyle, ...textColorStyle }}
    >
      {/*
        The three regions, and why each is styled the way it is, live in
        `WidgetCard.module.css` under "Fitting the content into a FIXED-HEIGHT
        card". Short version: the card's height comes from the grid and it clips,
        so the two UNBOUNDED text regions are clamped (with the full string on
        `title`) and the body — whose height is not ours to decide — scrolls.
      */}
      {showTitle
        ? (
          /*
            ── TITLE AND SUBJECT ICON ON ONE ROW (2026-08-19) ────────────────────
            Ticket owner: *"the icon on alerts, should be aligned to the title on
            top? like the fixed dashboard ones."*

            ⚠️ THE ICON HAD TO MOVE UP A LEVEL TO DO THIS, and that is the whole fix.

            It was rendered by the WIDGET, positioned against the widget's own box —
            which lives inside `.cardBody`, BELOW the title. From in there it could
            reach the top of the body and no further, so it always sat a title's
            height too low. No amount of offsetting fixes that honestly: the title's
            height is variable (it clamps to two lines), so any negative `top` would
            be a guess that breaks on the first card with a long name.

            The card owns the title, so the card owns what aligns with it. That is
            also how `DashboardIndicator` — the fixed tile these copy — is built:
            title and icon are one `Group`, value below.

            The card can decide this alone because everything it needs is on the
            definition: the TYPE says whether a subject icon is wanted, and
            `collection` says which one. No prop threading, and the widgets get
            simpler rather than more complex.
          */
          <div className={classes.cardHeader}>
            <Text
              // No `size` / `fw` props: the reference's title proportions (11.5px,
              // 600, uppercase, muted) live in `.cardTitle`, and a Mantine size prop
              // here would set an inline font-size that the module rule cannot beat.
              className={classes.cardTitle}
              // The full text, for a title the clamp shortened. Cheap, native, and
              // it works for a READER, who has no inspector to open.
              title={definition?.title}
            >
              {definition?.title}
            </Text>

            {subjectBadge}
          </div>
        )
        : null}
      {chrome}
      <div className={classes.cardBody}>
        {children}
      </div>
      {definition?.description ?
        <Text
          size="xs"
          c="dimmed"
          mt='xs'
          className={classes.cardDescription}
          title={definition.description}
        >
          {definition.description}
        </Text>
        : null
      }
    </div>
  );
}
